
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  TextField,
  ThemeProvider,
  createTheme,
  CircularProgress,
  Divider,
  Alert,
  Tooltip,
  Zoom,
  Fade,
  Checkbox,
  FormControlLabel,
  Stack
} from '@mui/material';


import {
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

import { Stepper, Step, StepLabel, StepContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import GetAppIcon from '@mui/icons-material/GetApp';
import ExtensionIcon from '@mui/icons-material/Extension';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ChromeExtensionGuide = ({ setTutorialOpen, tutorialOpen }) => {
    const [activeStep, setActiveStep] = useState(0);
  
    const handleClose = () => {
      setActiveStep(0); // Reset steps when closing
      setTutorialOpen(false);
    };
  
    const handleNext = () => {
      if (activeStep === 0) {
        window.open("https://drive.google.com/file/d/1edt8Lz8Cj2GZftXCdeZanu7x37x6QPsL/view?usp=sharing");
      }
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };
  
    const handleBack = () => {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };
  
    const installationSteps = [
      {
        label: 'Download the Extension Files',
        description: 'Click the button below to download the extension folder from Google Drive. Then, extract the ZIP file if necessary.',
        action: 'Download',
        img: '/images/step1_extension.png',
        link: 'https://drive.google.com/file/d/1edt8Lz8Cj2GZftXCdeZanu7x37x6QPsL/view?usp=sharing',
      },
      {
        label: 'Enable Developer Mode',
        description: 'Go to chrome://extensions in your Chrome browser and enable the Developer mode toggle in the top right corner.',
        action: 'Next',
        img: '/images/step2.1_extension.jpeg',
      },
      {
        label: 'Load the Unpacked Extension',
        description: 'Click "Load unpacked", select the extracted folder, and confirm. The extension will now appear in your browser.',
        action: 'Finish',
        img: '/images/step3_extension.jpeg',
      },
    ];
  
    // Custom stepIcon styling based on status
    const CustomStepIcon = (props) => {
      const { active, completed, icon } = props;
  
      return (
        <Box
          sx={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            backgroundColor: completed ? '#4caf50' : active ? '#4361ee' : '#e0e0e0',
            color: '#fff',
            fontWeight: 'bold',
            boxShadow: active ? '0 0 0 4px rgba(67, 97, 238, 0.2)' : 'none',
            transition: 'all 0.3s ease',
          }}
        >
          {completed ? <CheckIcon /> : icon}
        </Box>
      );
    };
  
    return (
      <Box
        sx={{
          display: tutorialOpen ? 'flex' : 'none',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1200,
          p: 2,
          backdropFilter: 'blur(4px)',
        }}
      >
        <Paper
          elevation={24}
          sx={{
            width: '100%',
            maxWidth: 720,
            maxHeight: '90vh',
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}
        >
          {/* Fixed position close button that's always in the corner */}
          <IconButton
            onClick={handleClose}
            aria-label="close"
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              zIndex: 10,
              color: 'grey.700',
              bgcolor: 'rgba(255,255,255,0.8)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.95)',
                color: 'error.main',
              },
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            }}
          >
            <CloseIcon />
          </IconButton>
  
          {/* Content container with its own scrolling */}
          <Box
            sx={{
              height: '90vh',
              maxHeight: 700,
              overflow: 'auto',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '4px',
              },
            }}
          >
            <Box sx={{ p: { xs: 3, sm: 4 }, pt: { xs: 6, sm: 7 } }}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 600,
                  color: '#1a237e',
                }}
              >
                <ExtensionIcon sx={{ mr: 2, color: '#4361ee', fontSize: 32 }} />
                Install Truth Detector Extension
              </Typography>
  
              <Alert
                severity="info"
                variant="filled"
                sx={{
                  mt: 3,
                  mb: 4,
                  borderRadius: 2,
                  bgcolor: 'rgba(67, 97, 238, 0.9)',
                }}
              >
                <Typography variant="body1" sx={{ color: 'white' }}>
                  Get the full power of our Truth Detector by installing it directly in your browser!
                  Analyze any webpage with just one click.
                </Typography>
              </Alert>
  
              <Stepper
                activeStep={activeStep}
                orientation="vertical"
                sx={{
                  mb: 3,
                  '& .MuiStepConnector-line': {
                    minHeight: 40,
                    borderLeftWidth: 2,
                  },
                  '& .MuiStepConnector-root': {
                    marginLeft: 2.5,
                  },
                  '& .MuiStepConnector-active': {
                    '& .MuiStepConnector-line': {
                      borderColor: '#4361ee',
                    },
                  },
                  '& .MuiStepConnector-completed': {
                    '& .MuiStepConnector-line': {
                      borderColor: '#4caf50',
                    },
                  },
                }}
              >
                {installationSteps.map((step, index) => (
                  <Step key={step.label}>
                    <StepLabel
                      StepIconComponent={CustomStepIcon}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: index === activeStep ? 600 : 400,
                          fontSize: '1.05rem',
                          color: index < activeStep ? '#2e7d32' :
                            index === activeStep ? '#1a237e' :
                              'text.secondary',
                          transition: 'color 0.3s ease',
                        }}
                      >
                        {step.label}
                      </Typography>
                    </StepLabel>
                    <StepContent
                      sx={{
                        borderLeft: '2px solid',
                        borderColor: index === activeStep ? '#4361ee' : 'rgba(0, 0, 0, 0.12)',
                        ml: 2.5,
                        pl: 2.5,
                        pb: 3,
                      }}
                    >
                      <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                        {step.description}
                      </Typography>
  
                      {(
                        <Box
                          sx={{
                            width: '100%',
                            height: 220,
                            bgcolor: 'rgba(67, 97, 238, 0.04)',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 3,
                            border: '1px dashed rgba(67, 97, 238, 0.3)',
                            backgroundSize: '20px 20px',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundSize: '20px 20px',
                              animation: 'moveBackground 30s linear infinite',
                            },
                            '@keyframes moveBackground': {
                              '0%': {
                                backgroundPosition: '0 0',
                              },
                              '100%': {
                                backgroundPosition: '40px 40px',
                              },
                            },
                          }}
                        >
                          {step.img && (
                            <img
                              src={step.img}
                              alt={`Step ${index + 1} Screenshot`}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                zIndex: 1
                              }}
                            />
                          )}
                        </Box>
                      )}
  
                      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                        <Button
                          variant="contained"
                          size="large"
                          onClick={
                            index === installationSteps.length - 1 ? handleClose : handleNext
                          }
                          startIcon={
                            index === 0 ? <GetAppIcon /> :
                              index === installationSteps.length - 1 ? <CheckCircleIcon /> :
                                <ArrowForwardIcon />
                          }
                          sx={{
                            bgcolor: index === installationSteps.length - 1 ? '#4caf50' : '#4361ee',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                            '&:hover': {
                              bgcolor: index === installationSteps.length - 1 ? '#388e3c' : '#3949ab',
                              boxShadow: '0 6px 12px rgba(0,0,0,0.2)',
                              transform: 'translateY(-2px)',
                            },
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            transition: 'transform 0.2s ease',
                          }}
                        >
                          {step.action}
                        </Button>
                        {index > 0 && (
                          <Button
                            onClick={handleBack}
                            size="large"
                            startIcon={<ArrowBackIcon />}
                            sx={{
                              color: '#4361ee',
                              '&:hover': {
                                bgcolor: 'rgba(67, 97, 238, 0.08)',
                              },
                              px: 3,
                            }}
                          >
                            Back
                          </Button>
                        )}
                      </Box>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
  
              {activeStep === installationSteps.length && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    Installation Complete!
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Your Truth Detector extension is now ready to use. Enjoy enhanced browsing!
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClose}
                    sx={{ mt: 2 }}
                  >
                    Get Started
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  };

  export default ChromeExtensionGuide;